import React, {FC} from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";

import TypeSelector from "./type_selector";
import ContainedTextField from "../../contained_text_field";
import TypeDetail from "./type_detail";
import isPossibleEnumType from "./util/is_possible_enum_type";
import isOptionalType from "./util/is_optional_type";
import getCoreTypeFromOptionalType from "./util/get_core_type_from_optional_type";
import {StringArgEditor} from "../string";

import {
  SchemaOptionalType,
  SchemaPropertyWithOptionalType,
  SchemaType,
} from "common/flowmaster/types/schema";
import {
  ActionDefinition,
  WorkflowTask,
} from "common/flowmaster/types/task_config";
import {
  Prompt,
  WorkflowContext,
  WorkflowInputs,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";

interface PropertyEditorProps {
  newProperty?: boolean;
  name?: string;
  property?: SchemaPropertyWithOptionalType;
  onUpdate?: (name: string, property: SchemaPropertyWithOptionalType) => void;
  onCreate?: (name: string, property: SchemaPropertyWithOptionalType) => void;
  onRemove?: () => void;
  isNested?: boolean;

  priorTasks?: WorkflowTask[];
  context?: WorkflowContext;
  inputs?: WorkflowInputs;
  prompts?: Prompt[];
  actionDefinitions?: ActionDefinition[];
  organisationId?: OrganisationId;
  useDataStoreProperties?: boolean;
}

const useStyles = makeStyles({
  container: (props: {isNested: boolean}) => ({
    backgroundColor: "#cccccc",
    margin: props.isNested ? ".3em 0" : ".6em 0",
    padding: props.isNested ? "5px 15px" : "15px 20px",
    borderRadius: "4px",
    border: props.isNested ? "2px solid #696969" : "none",
  }),
  inputContainer: {
    display: "flex",
    alignItems: "center",
  },
});

const PropertyEditor: FC<PropertyEditorProps> = ({
  name = "",
  property = {description: "", type: "string"},
  onUpdate = () => {},
  onRemove = () => {},
  isNested = false,
  priorTasks,
  context,
  inputs,
  prompts,
  actionDefinitions,
  organisationId,
  useDataStoreProperties = true,
}) => {
  if (
    (priorTasks === undefined ||
      context === undefined ||
      inputs === undefined ||
      prompts === undefined ||
      actionDefinitions === undefined ||
      organisationId === undefined) &&
    useDataStoreProperties
  ) {
    throw Error(
      "A prop required for using the schema editor on the workflow editor is undefined.",
    );
  }

  const classes = useStyles({isNested});
  const isEnum = Boolean(property.enum);
  const isOptional = isOptionalType(property.type);

  const handleTypeChange = (type: SchemaType) => {
    const updatedProperty: SchemaPropertyWithOptionalType = {
      ...property,
      type: isOptional ? [type, "null"] : type,
      description: property.description,
    };

    if (type === "object") {
      updatedProperty.properties = property.properties || {};
      delete updatedProperty.items;
    } else if (type === "array") {
      updatedProperty.items = property.items || {type: "string"};
      delete updatedProperty.properties;
    } else {
      delete updatedProperty.properties;
      delete updatedProperty.items;
    }

    if (!isPossibleEnumType(type)) {
      delete updatedProperty.enum;
    } else if (isEnum) {
      updatedProperty.enum = [];
    }

    onUpdate(name, updatedProperty);
  };

  const toggleEnum = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedProperty = {...property};
    if (event.target.checked && !updatedProperty.enum) {
      updatedProperty.enum = [];
    } else {
      delete updatedProperty.enum;
    }
    onUpdate(name, updatedProperty);
  };

  const toggleOptional = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedProperty = {...property};
    if (event.target.checked && !isOptionalType(property.type)) {
      updatedProperty.type = [property.type as SchemaType, "null"];
    } else {
      updatedProperty.type = (updatedProperty.type as SchemaOptionalType)[0];
    }
    onUpdate(name, updatedProperty);
  };

  const onEnumUpdate = (enumValues: SchemaPropertyWithOptionalType["enum"]) => {
    onUpdate(name, {
      ...property,
      enum: enumValues,
    });
  };

  return (
    <Grid
      item
      key={name}
      container
      alignItems="center"
      className={classes.container}
    >
      <Grid item xs={3}>
        <ContainedTextField
          label="Property Name"
          initialValue={name}
          onUpdate={updatedName => onUpdate(updatedName, property)}
        />
      </Grid>
      <Grid item xs={4}>
        {useDataStoreProperties ? (
          <StringArgEditor
            argValue={{
              value:
                typeof property.description === "object"
                  ? property.description
                  : {
                      source_type: "literal",
                      value: property.description || "",
                    },
            }}
            onUpdateItem={newArgValue => {
              if (newArgValue.value.source_type === "literal") {
                const literalValue = newArgValue.value.value;
                onUpdate(name, {...property, description: literalValue});
              } else {
                onUpdate(name, {
                  ...property,
                  description: newArgValue.value,
                });
              }
            }}
            priorTasks={priorTasks as WorkflowTask[]}
            context={context as WorkflowContext}
            inputs={inputs as WorkflowInputs}
            prompts={prompts as Prompt[]}
            actionDefinitions={actionDefinitions as ActionDefinition[]}
            organisationId={organisationId as OrganisationId}
          />
        ) : (
          <ContainedTextField
            label={"description"}
            initialValue={property.description as string}
            onUpdate={newValue =>
              onUpdate(name, {...property, description: newValue})
            }
            multiline={true}
          />
        )}
      </Grid>
      <Grid item xs={3}>
        <div className={classes.inputContainer}>
          <TypeSelector
            value={
              isOptional
                ? getCoreTypeFromOptionalType(
                    property.type as SchemaOptionalType,
                  )
                : (property.type as SchemaType)
            }
            onChange={handleTypeChange}
          />
          <div>
            {isPossibleEnumType(property.type) && (
              <FormControlLabel
                style={{marginLeft: "0.5em"}}
                control={<Checkbox checked={isEnum} onChange={toggleEnum} />}
                label="Enum"
              />
            )}
            <FormControlLabel
              style={{marginLeft: "0.5em"}}
              control={
                <Checkbox checked={isOptional} onChange={toggleOptional} />
              }
              label="Optional"
            />
          </div>
        </div>
      </Grid>
      <Grid item xs={2}>
        <IconButton onClick={() => onRemove()}>
          <DeleteIcon />
        </IconButton>
      </Grid>
      <TypeDetail
        name={name}
        property={property}
        isEnum={isEnum}
        onUpdate={onUpdate}
        onEnumUpdate={onEnumUpdate}
        priorTasks={priorTasks}
        context={context}
        inputs={inputs}
        prompts={prompts}
        actionDefinitions={actionDefinitions}
        organisationId={organisationId}
        useDataStoreProperties={useDataStoreProperties}
      />
    </Grid>
  );
};

export default PropertyEditor;
